import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link as LinkIcon, DollarSign, Share2, ChevronRight, X, Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * "Become an Affiliate" quick tour — 3 short steps that explain how the
 * affiliate system works before sending the user to the explore page.
 *
 * Used as a modal triggered by the buyer dashboard CTA. Light enough to
 * dismiss but informative enough that a brand-new buyer understands what
 * they're signing up for. Brand colors only (cyan/black/white/green).
 */
const STEPS = [
  {
    icon: Share2,
    title: 'Pick a tool you actually use',
    body: (
      <>
        Browse our marketplace and grab an affiliate link from any creator
        with a program enabled. <span className="text-white font-semibold">No application, no waiting</span> — one click and you've got
        your unique link.
      </>
    ),
    accent: '#00F0FF',
  },
  {
    icon: LinkIcon,
    title: 'Share it anywhere',
    body: (
      <>
        Drop it in your bio, newsletter, YouTube description, X/LinkedIn posts,
        Discord — wherever your audience is. Every click is tracked back to you for
        up to <span className="text-white font-semibold">30 days</span>.
      </>
    ),
    accent: '#00F0FF',
  },
  {
    icon: DollarSign,
    title: 'Get paid every time someone buys',
    body: (
      <>
        Earn up to <span className="text-[#00FF94] font-bold">30% commission</span> on every sale your link refers. Track clicks, conversions, and earnings live from your affiliate dashboard. Cash out via Stripe.
      </>
    ),
    accent: '#00FF94',
  },
];

export default function AffiliateTourModal({ open, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  if (!open) return null;
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  const close = () => { setStep(0); onClose(); };
  const next = () => {
    if (isLast) {
      close();
      navigate('/affiliate/program');
    } else {
      setStep((s) => s + 1);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="affiliate-tour-modal"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="relative w-full sm:max-w-md bg-[#0a0a0a] border-t sm:border border-white/10 sm:rounded-2xl shadow-2xl overflow-hidden">
        {/* Close */}
        <button
          onClick={close}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
          aria-label="Close"
          data-testid="affiliate-tour-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Progress dots */}
        <div className="flex gap-1.5 px-6 pt-6">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-[#00F0FF]' : 'bg-white/10'}`}
              data-testid={`tour-progress-${i}`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-6 py-8 text-center">
          <div
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-5"
            style={{
              background: `${current.accent}15`,
              border: `1px solid ${current.accent}40`,
            }}
          >
            <Icon className="w-8 h-8" style={{ color: current.accent }} />
          </div>
          <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#00F0FF] mb-2">
            <Sparkles className="w-3 h-3" /> Step {step + 1} of {STEPS.length}
          </p>
          <h2 className="text-xl sm:text-2xl font-black text-white mb-3 leading-tight">
            {current.title}
          </h2>
          <p className="text-sm sm:text-base text-neutral-400 leading-relaxed max-w-sm mx-auto">
            {current.body}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2 flex items-center gap-3" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
          {step > 0 ? (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="text-sm text-neutral-400 hover:text-white px-4 py-3"
              data-testid="affiliate-tour-back"
            >
              Back
            </button>
          ) : (
            <button
              onClick={close}
              className="text-sm text-neutral-500 hover:text-white px-4 py-3"
              data-testid="affiliate-tour-skip"
            >
              Skip
            </button>
          )}
          <Button
            onClick={next}
            className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold rounded-xl py-6 text-sm"
            data-testid="affiliate-tour-next"
          >
            {isLast ? (
              <>Explore programs <Check className="w-4 h-4 ml-2" /></>
            ) : (
              <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
